import React, { useState, useEffect } from "react";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { hasSubcategories, subcategoriesFor } from "../constants/subcategories";
import { platformAPI } from "../services/api";
import JuiceDetailsFields, {
  DEFAULT_JUICE_DETAILS,
  parseJuiceDetails,
  serializeJuiceDetails,
} from "./JuiceDetailsFields";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:3010/api";

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

/**
 * Create or edit a range (variant group): the shared face of a family of
 * products. Its fields are copied into each product when it is added, so the
 * form says as much.
 */
export default function RangeForm({ templateId, group, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: "",
    category: "juice",
    brand: "",
    subcategory: "",
    price: "",
  });
  const [juiceDetails, setJuiceDetails] = useState(DEFAULT_JUICE_DETAILS);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!group) return;
    setFormData({
      name: group.name || "",
      category: group.category || "juice",
      brand: group.brand || "",
      subcategory: group.subcategory || "",
      price: group.price ?? "",
    });
    if (group.juice_details) setJuiceDetails(parseJuiceDetails(group.juice_details));
    if (Array.isArray(group.images)) setExistingImages(group.images);
  }, [group]);

  const handleImageSelect = (e) => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const clearNewImages = () => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setImagePreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("name", formData.name.trim());
      body.append("category", formData.category);
      body.append("brand", formData.brand || "");
      body.append("subcategory", formData.subcategory || "");
      body.append("price", formData.price === "" ? "" : String(formData.price));
      if (formData.category === "juice") {
        body.append("juice_details", JSON.stringify(serializeJuiceDetails(juiceDetails)));
      }
      selectedFiles.forEach((file) => body.append("images", file));

      if (group) {
        body.append("existingImages", JSON.stringify(existingImages));
        await platformAPI.updateTemplateGroup(templateId, group.id, body);
      } else {
        await platformAPI.createTemplateGroup(templateId, body);
      }
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ marginTop: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-0 sm:p-4"
    >
      <div className="bg-white sm:rounded-xl shadow-2xl max-w-3xl w-full max-h-[100vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">{group ? "Edit range" : "New range"}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <p className="text-sm text-gray-500">
            A range is a family of products, such as one device in several flavours or colours. What
            you fill in here is copied into each product when it is added; every product keeps its own
            name, price and pictures, which you can still change one by one.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Range name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={inputClass}
                placeholder="e.g. Vozol Gear 12000"
                required
              />
            </div>
            <div>
              <label className={labelClass}>Brand</label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value, subcategory: "" })
                }
                className={inputClass}
                required
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            {hasSubcategories(formData.category) && (
              <div>
                <label className={labelClass}>Subcategory</label>
                <select
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className={inputClass}
                >
                  <option value="">No subcategory</option>
                  {subcategoriesFor(formData.category).map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className={labelClass}>Price</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pictures</h3>
            {existingImages.length > 0 && (
              <div className="mb-4">
                <label className={labelClass}>Current pictures</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {existingImages.map((img, index) => (
                    <div key={img} className="relative group">
                      <img
                        src={`${API_ORIGIN}${img}`}
                        alt=""
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setExistingImages((prev) => prev.filter((_, i) => i !== index))}
                        aria-label="Remove picture"
                        className="absolute top-2 end-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <label className={labelClass}>{group ? "Add pictures" : "Upload pictures"}</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF or WebP, up to 5MB each.</p>
            {imagePreviews.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">New (preview)</label>
                  <button
                    type="button"
                    onClick={clearNewImages}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {imagePreviews.map((preview) => (
                    <img
                      key={preview}
                      src={preview}
                      alt=""
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {formData.category === "juice" && (
            <JuiceDetailsFields value={juiceDetails} onChange={setJuiceDetails} />
          )}

          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {loading ? "Saving…" : group ? "Save range" : "Create range"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
